<script>
    import { onMount, tick } from 'svelte';
    import { push } from 'svelte-spa-router';
    import { getApiBase } from './apiBase.js';
    import ClassName from './ClassNameComponent.svelte';
    import TeacherName from './TeacherNameComponent.svelte';
    import TimeShower from './TimeShowerComponent.svelte';
    import CancelReason from './CancelReasonComponentsvelte.svelte';

    export let params;
    const apiBase = getApiBase();
    const warning = 'Atšaukus pamoką tą pačią dieną, už ją vis tiek reikės sumokėti. Ar tikrai norite atšaukti pamoką?';
    let classinfo = {};
    let students = [];
    let selectedStudentId = '';
    let isUserTeacher = false;
    let requiresAcknowledgement = false;
    let loading = true;
    let isSubmitting = false;
    let error = '';
    let reason = '';
    let showPaymentDialog = false;
    let paymentDialog;
    let backButton;
    let returnFocus;
    $: selectedStudent = students.find(student => student.id === Number(selectedStudentId));
    $: alreadyCancelled = selectedStudent?.participation_status === 'cancelled_payable';
    $: dateParts = (classinfo.time || '').split(' ');

    function headers() {
        return { Authorization: `Bearer ${localStorage.getItem('jwtToken')}`, 'Content-Type': 'application/json' };
    }

    async function loadDetails() {
        loading = true;
        error = '';
        try {
            const response = await fetch(`${apiBase}/studentplayground/cancelEvent/${params.id}/`, { headers: headers() });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Nepavyko įkelti pamokos.');
            classinfo = data.classinfo;
            students = data.students || [];
            isUserTeacher = data.is_teacher;
            requiresAcknowledgement = data.requires_payment_acknowledgement;
            selectedStudentId = students.length === 1 ? students[0].id : '';
        } catch (failure) {
            error = failure.message || 'Nepavyko įkelti pamokos.';
        } finally {
            loading = false;
        }
    }
    onMount(loadDetails);

    async function openPaymentDialog() {
        showPaymentDialog = true;
        await tick();
        paymentDialog.showModal();
        backButton.focus();
    }

    function closePaymentDialog() {
        if (isSubmitting) return;
        paymentDialog.close();
        showPaymentDialog = false;
        error = '';
        returnFocus?.focus();
    }

    async function handleCancelSubmit(event) {
        if (isSubmitting) return;
        error = '';
        reason = event.detail.reason;
        if (!isUserTeacher && !selectedStudent) {
            error = 'Pasirinkite, kurio mokinio pamoką atšaukiate.';
            return;
        }
        if (!isUserTeacher && !reason.trim()) {
            error = 'Prašome įrašyti atšaukimo priežastį.';
            return;
        }
        returnFocus = document.activeElement;
        if (requiresAcknowledgement) await openPaymentDialog();
        else await submitCancellation(false);
    }

    async function submitCancellation(acknowledgePayment) {
        if (isSubmitting) return;
        isSubmitting = true;
        error = '';
        let showUpdatedPolicy = false;
        try {
            const body = { reason };
            if (!isUserTeacher) {
                body.student_id = Number(selectedStudentId);
                body.acknowledge_payment = acknowledgePayment;
            }
            const response = await fetch(`${apiBase}/studentplayground/cancelEvent/${params.id}/`, {
                method: 'POST', headers: headers(), body: JSON.stringify(body),
            });
            const data = await response.json();
            if (response.status === 409 && data.code === 'payment_acknowledgement_required') {
                requiresAcknowledgement = true;
                showUpdatedPolicy = !showPaymentDialog;
            } else if (!response.ok) {
                throw new Error(data.error || 'Nepavyko atšaukti pamokos. Bandykite dar kartą.');
            } else {
                if (paymentDialog?.open) paymentDialog.close();
                push('/schedule?tab=reschedule');
            }
        } catch (failure) {
            error = failure.message || 'Nepavyko atšaukti pamokos. Bandykite dar kartą.';
        } finally {
            isSubmitting = false;
        }
        if (showUpdatedPolicy) await openPaymentDialog();
    }
</script>

<div class="top-bar">
    <a href="#/schedule" class="exit-button" aria-label="Grįžti į tvarkaraštį"><img src="/static/icons/backButtonIcon.png" alt="" class="icon" /></a>
    <div class="label">Atšaukti pamoką</div><div class="filler"></div>
</div>

<div class="classcontainer">
    {#if loading}
        <p role="status">Įkeliama pamoka...</p>
    {:else if classinfo.name}
        <ClassName name={classinfo.name} />
        <TeacherName teacher={classinfo.teacher} />
        <TimeShower time={dateParts[1] || ''} date={`${dateParts[2] || ''}<br>${dateParts[0] || ''}`} />
        {#if !isUserTeacher && students.length > 1}
            <label for="cancellation-student">Mokinys</label>
            <select id="cancellation-student" bind:value={selectedStudentId} disabled={isSubmitting}>
                <option value="">Pasirinkite mokinį</option>
                {#each students as student}<option value={student.id}>{student.name}</option>{/each}
            </select>
        {/if}
        {#if alreadyCancelled}
            <p role="status">Atšaukta nesilaikant atšaukimo sąlygų. Pamoka apmokama.</p>
        {:else}
            <CancelReason requireReason={!isUserTeacher} {isSubmitting} on:submit={handleCancelSubmit} />
        {/if}
    {:else}
        <button type="button" on:click={loadDetails}>Bandyti dar kartą</button>
    {/if}
    {#if error && !showPaymentDialog}<p role="alert">{error}</p>{/if}
</div>

{#if showPaymentDialog}
    <dialog bind:this={paymentDialog} role="alertdialog" aria-labelledby="payment-warning-title" aria-describedby="payment-warning-message"
        on:cancel={(event) => { event.preventDefault(); closePaymentDialog(); }}>
        <h2 id="payment-warning-title">Pamokos atšaukimas</h2>
        {#if selectedStudent}<p>{selectedStudent.name}</p>{/if}
        <p id="payment-warning-message">{warning}</p>
        {#if error}<p role="alert">{error}</p>{/if}
        <div class="dialog-actions">
            <button type="button" bind:this={backButton} on:click={closePaymentDialog} disabled={isSubmitting}>Grįžti</button>
            <button type="button" on:click={() => submitCancellation(true)} disabled={isSubmitting} aria-busy={isSubmitting}>{isSubmitting ? 'Atšaukiama...' : 'Taip, atšaukti pamoką'}</button>
        </div>
    </dialog>
{/if}
<style>
    dialog { border: 1px solid #14695b; border-radius: 8px; padding: 24px; max-width: 480px; width: calc(100% - 32px); box-sizing: border-box; color: #222; }
    dialog::backdrop { background: rgb(0 0 0 / 45%); }
    dialog h2 { font-size: 1.3rem; }
    .dialog-actions { display: flex; gap: 12px; flex-wrap: wrap; }
    .dialog-actions button, select { padding: 12px; font: inherit; }
    .dialog-actions button:last-child { background: #fab45a; border: 1px solid #333; }
    .dialog-actions button:last-child:disabled { background-color: var(--cancellation-pending-background, #6b7280) !important; color: var(--cancellation-pending-color, #fff) !important; cursor: wait; }
    [role="alert"] { color: #9b2424; }

.top-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    background-color: transparent;
    border-bottom: 1px solid #000000;
    margin-bottom: 40px;
  
  }
  .filler {
    width:50px;
  }

  .label {
    font-size: x-large;
    color:#14695b;
    
  }

  .exit-button {
    max-width: 50px;
  }

  .icon {
    width: 50px;
    height: 50px;
  }
p {
    font-size: 1.2em;
}
.classcontainer{
    padding: 15px;
    background-color: #eeebdb;
    display: flex;
    flex-direction: column;
    margin-bottom:15px;
    gap: 10px;
    box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
    border-radius: 8px;
    flex: none;
    color:black;
    align-items: flex-start;

  }
</style>
